import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingOptions,
    DrawingSettingGroup,
    DrawingTypes,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'

export type IndicatorLineDrawingType = 'simpleIndicatorLine' | 'infoIndicatorLine'

export type IndicatorLineDrawingState = {
    points: [Point, Point, string][]
    labels?: string[]
}

export type IndicatorLineToolOptions = {
    drawingType: IndicatorLineDrawingType
}

export type IndicatorLineDrawingSettings = {
    lineType: LineTypes,
    indicator: boolean,
    lineColor: ColorValue,
    lineWidth: number,
    data: [Point, Point, string][],
    statsPosition: string,
    showStats: boolean,
    seriesGroupId?: string
}

export type ScaledCoordinateType = { p1x: number, p2x: number, p1y: number, p2y: number, label: string }

class IndicatorLineDrawingTool extends DrawingBase<IndicatorLineToolOptions, IndicatorLineDrawingSettings, IndicatorLineDrawingState> {
    slopes: number[] = []
    rotations: number[] = []
    intercepts: number[] = []
    scaledCoordinates: ScaledCoordinateType[] = []

    constructor(options: DrawingOptions<IndicatorLineToolOptions, IndicatorLineDrawingSettings, IndicatorLineDrawingState>) {
        super(options)
        this._state.points = options.settings.data ?? []
        setTimeout(() => {
            this.primitive?.requestUpdate()
        }, 0)

    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        this.scaledCoordinates.forEach((scaledCoordinate) => {
            let start = this.getStartCoordinate(scaledCoordinate)
            let end = this.getEndCoordinate(scaledCoordinate)

            if (!start || !end) return

            ctx.beginPath()
            this.setLineAttributes(ctx)
            ctx.moveTo(start.x, start.y)
            ctx.lineTo(end.x, end.y)
            ctx.stroke()
            ctx.closePath()

            const callText = scaledCoordinate.label
            let padding: number = 0, verticalSpacing: number = 8
            ctx.lineWidth = 0.5
            const dpr = (window.devicePixelRatio || 1)
		    const fontSize = 11 * dpr
            ctx.font = `${fontSize}px sans-serif`
            let boundingWidth = 0, boundingHeight = 0
            let callTextDimensions = ctx.measureText(callText)
            boundingWidth = Math.max(boundingWidth, callTextDimensions.width)
            const callTextHeight = callTextDimensions.fontBoundingBoxAscent + callTextDimensions.fontBoundingBoxDescent
            boundingHeight += callTextHeight
            boundingWidth += 2 * padding
            boundingHeight += 2 * padding

            ctx.textBaseline = 'top'
            ctx.fillStyle = this._settings.lineColor.color
            const textWidth = ctx.measureText(scaledCoordinate.label).width
            ctx.fillText(scaledCoordinate.label, start.x - textWidth, start.y)

        })


    };

    calculateLineAttributes() {
        const { points } = this._state
        if (!points || (points.length === 0)) return

        const newScaledCoordinates: ScaledCoordinateType[] = []
        const newSlopes: typeof this.slopes = []
        const newIntercepts: typeof this.intercepts = []
        const newRotations: typeof this.rotations = []

        points.forEach((point) => {
            let p1x = this.timeToCoordinate(point[0].x)
            let p2x = this.timeToCoordinate(point[1].x)
            let p1y = this.priceToCoordinate(point[0].y)
            let p2y = this.priceToCoordinate(point[1].y)
            if (p1x && p2x && p1y && p2y) {
                p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
                p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
                p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
                p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
                newScaledCoordinates.push({ p1x, p1y, p2x, p2y, label: point[2] })
                const slope = (p2y - p1y) / (p2x - p1x)
                newSlopes.push(slope)
                newIntercepts.push(p1y - slope * p1x)
                newRotations.push(Math.atan(slope))
            }
        })
        this.scaledCoordinates = newScaledCoordinates
        this.slopes = newSlopes
    }

    getStartCoordinate(scaledCoordinate: ScaledCoordinateType): PointCoordinates | void {
        let { p1x, p1y } = scaledCoordinate

        let start: PointCoordinates = {
            x: p1x,
            y: p1y
        }
        return start
    }

    getEndCoordinate(scaledCoordinate: ScaledCoordinateType): PointCoordinates | void {
        let { p2x, p2y } = scaledCoordinate

        let end: PointCoordinates = {
            x: p2x,
            y: p2y
        }

        return end
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max(this._settings.lineWidth * 3, 8), 3]
        } else if (this._settings.lineType === 'dotted') {
            const dotWidth = Math.max(2, this._settings.lineWidth)
            lineDash = [dotWidth, dotWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    onSettingsUpdate = (settings: Partial<IndicatorLineDrawingSettings>) => {
        if (this._settings.data) {
            this._state.points = this._settings.data?.map((point) => [point[0], point[1], point[2]])
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<IndicatorLineDrawingSettings, SettingValues>[] => {
        const { drawingType } = this.options
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#646464',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }
        ]
    }

    getSettingsValues(): IndicatorLineDrawingSettings {
        const settings = { ...this._settings }
        settings.data = this._state.points
        return settings
    }

    getDrawingType(): DrawingTypes {

        switch (this.options.drawingType) {
            case 'simpleIndicatorLine':
                return 'simpleIndicatorLine'
            case 'infoIndicatorLine':
                return 'infoIndicatorLine'

        }
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof IndicatorLineDrawingSettings)[]
    }
}

export default IndicatorLineDrawingTool